/**
 * @file dateUtils.js
 * @location src/utils/dateUtils.js
 * @role 날짜 관련 유틸리티 상수 · 함수
 *
 * getTodayStr  현재 날짜 문자열 'YYYY-MM-DD' 를 호출 시점에 계산해 반환 (자정 넘김·테스트 시간조작 대응)
 * isOverdue    기한 초과 여부 판단 — 미완료(done 제외)이고 due < today 이면 true
 */
import { isClosed } from '../data/labels.js';

/** YYYY-MM-DD 문자열 생성 (로컬 시각 기준) */
function makeDateStr(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * 'YYYY-MM-DD' 호출 시점 계산.
 * 자정 경계·테스트 `vi.setSystemTime()` 모두 반영하므로 비교·표시 양쪽에서 이 함수를 사용.
 */
export const getTodayStr = () => makeDateStr();

/** 기한 초과 여부: 종결(완료·중지)된 태스크는 false, due 미설정이면 false */
export const isOverdue = (due, status) =>
  !isClosed(status) && !!due && due < getTodayStr();

/**
 * 인라인 표시용 단축 날짜 'M/D (요일)' — 캘린더 셀 툴팁·MonthView pile.
 * 저장 비교용에는 사용하지 않음 (getTodayStr 사용).
 * 입력은 'YYYY-MM-DD' 또는 ISO. 파싱 실패 시 원문 반환.
 */
export const fmtShort = (dateStr) => {
  if (!dateStr) return '';
  const iso = dateStr.length === 10 ? `${dateStr}T00:00:00` : dateStr;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return dateStr;
  return `${d.getMonth() + 1}/${d.getDate()} (${['일','월','화','수','목','금','토'][d.getDay()]})`;
};

/**
 * 풀 날짜 'YYYY년 M월 D일 (요일)' — 페이지 헤더 등 (기본=오늘).
 * 입력은 'YYYY-MM-DD' 또는 ISO. 파싱 실패 시 원문 반환.
 */
export const fmtLong = (dateStr = getTodayStr()) => {
  const iso = dateStr.length === 10 ? `${dateStr}T00:00:00` : dateStr;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return dateStr;
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 (${['일','월','화','수','목','금','토'][d.getDay()]})`;
};

/**
 * 기한(due) 컨텍스트 라벨 — 「오늘·내일·어제·N일 후·N일 전」 (7일 이내) / 「M/D (요일)」 (그 이상).
 * 빈 due 또는 파싱 실패 시 원문 반환. 매일 보는 task 카드에서 인지 부하 ↓.
 */
export const formatDueLabel = (due, today = getTodayStr()) => {
  if (!due) return '';
  if (due === today) return '오늘';
  const d = new Date(`${due}T00:00:00`);
  const t = new Date(`${today}T00:00:00`);
  if (!Number.isFinite(d.getTime()) || !Number.isFinite(t.getTime())) return due;
  const diffDays = Math.round((d - t) / 86400000);
  if (diffDays === 1)  return '내일';
  if (diffDays === -1) return '어제';
  if (diffDays > 1  && diffDays <= 6)  return `${diffDays}일 후`;
  if (diffDays < -1 && diffDays >= -6) return `${-diffDays}일 전`;
  return fmtShort(due);
};

/**
 * D-day 라벨 — 업무가 「오늘 기준」이므로 절대 날짜 대신 상대 카운트다운으로 표시.
 *   초과: 'N일 초과'(over) / 당일: '오늘'(today) / 미래: 'N일 남음'(future) / due 없음: '—'(none)
 * 표시 전용 — 기한 초과 판정(isOverdue) 로직과 무관.
 * @param {string} due    'YYYY-MM-DD'
 * @param {string} [today] 'YYYY-MM-DD' (기본 = 오늘)
 * @returns {{ label: string, tone: 'over'|'today'|'future'|'none' }}
 */
export const ddayLabel = (due, today = getTodayStr()) => {
  if (!due) return { label: '—', tone: 'none' };
  const d = new Date(`${due}T00:00:00`);
  const t = new Date(`${today}T00:00:00`);
  if (!Number.isFinite(d.getTime()) || !Number.isFinite(t.getTime())) return { label: due, tone: 'none' };
  const diff = Math.round((d - t) / 86400000);
  if (diff < 0)  return { label: `${-diff}일 초과`, tone: 'over' };
  if (diff === 0) return { label: '오늘', tone: 'today' };
  return { label: `${diff}일 남음`, tone: 'future' };
};

/** 'YYYY-MM-DD' 에 n 일을 더해 같은 형식으로 반환 (로컬 시각 기준) */
export function addDays(ymd, n) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

/** Date 객체를 'YYYY-MM-DD' 형식 문자열로 포맷 (로컬 시각 기준) */
export const formatDate = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** 주어진 날짜가 속한 ISO 주의 월요일 'YYYY-MM-DD' (로컬 시각 기준) */
export const weekStart = (d = new Date()) => {
  const base = new Date(d);
  const day = base.getDay();            // 0=일 ... 6=토
  const offset = day === 0 ? -6 : 1 - day; // 월요 기준
  base.setDate(base.getDate() + offset);
  return formatDate(base);
};

/**
 * 다음 영업일(월~금) 'YYYY-MM-DD' 반환. 입력일 자체는 포함하지 않고 다음 평일로 진행.
 * 당일 이월 자동화에서 사용 — 토·일을 건너뛰어 다음 평일로 이월.
 */
export const nextBusinessDay = (fromStr) => {
  const base = fromStr ? new Date(`${fromStr}T00:00:00`) : new Date();
  do {
    base.setDate(base.getDate() + 1);
  } while (base.getDay() === 0 || base.getDay() === 6);
  return formatDate(base);
};

/** 'YYYY-MM-DD' 에서 영업일 n 칸 전진. n=0 이면 입력 그대로 (호출부가 영업일 보장). */
export function addBusinessDays(ymd, n) {
  let out = ymd;
  for (let i = 0; i < n; i++) out = nextBusinessDay(out);
  return out;
}

const WEEKDAY_KR = ['일', '월', '화', '수', '목', '금', '토'];

/**
 * 다음 영업일 + 표시 라벨. 다음 영업일이 달력상 "내일"이면 '내일', 아니면 'X요일'.
 * 금·토 → 월요일이라 '월요일', 일 → 월(내일)이라 '내일'.
 * @param {string} [today] YYYY-MM-DD
 * @returns {{ date: string, label: string }}
 */
export const nextBizLabel = (today = getTodayStr()) => {
  const date = nextBusinessDay(today);
  if (date === addDays(today, 1)) return { date, label: '내일' };
  const wd = WEEKDAY_KR[new Date(`${date}T00:00:00`).getDay()];
  return { date, label: `${wd}요일` };
};

/**
 * 'YYYY-MM-DD' (또는 Date) → '2026-Q2' 형식 분기 문자열.
 * OKR 의 분기 라벨 (1~3월=Q1, 4~6=Q2, 7~9=Q3, 10~12=Q4) 과 일치.
 * 입력이 비어있으면 오늘 기준 분기 반환.
 */
export const toQuarter = (dateStr) => {
  const d = dateStr ? new Date(`${dateStr}T00:00:00`) : new Date();
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `${d.getFullYear()}-Q${q}`;
};
